import { supabase } from './client';
import type { User, Session, AuthError } from '@supabase/supabase-js';

// Export types for use in store
export type { User, Session, AuthError };

function resolveFrontendOrigin(): string {
    if (typeof window === 'undefined') {
        return '';
    }
    // Always use the active browser origin to prevent stale env/runtime config
    // from sending OAuth flows to a different environment.
    return window.location.origin;
}

export function getOAuthRedirectUrl(path = '/auth/callback'): string {
    const origin = resolveFrontendOrigin();
    if (!origin) return '';
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${origin}${normalizedPath}`;
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
    });

    return { result: data, error };
}

/**
 * Sign up with email and password
 */
export async function signUpWithEmail(email: string, password: string, name?: string) {
    const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
            data: {
                name,
            },
        },
    });

    return { result: data, error };
}

/**
 * Sign out
 */
export async function signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
}

/**
 * Get current session
 */
export async function getSession() {
    const { data, error } = await supabase.auth.getSession();
    return { session: data.session, error };
}

/**
 * Get current user
 */
export async function getUser() {
    const { data, error } = await supabase.auth.getUser();
    return { user: data.user, error };
}

/**
 * Reset password (send email)
 */
export async function resetPassword(email: string) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/update-password`,
    });
    if (error) throw error;
}

/**
 * Update password (logged in user or after reset link)
 */
export async function updatePassword(password: string) {
    const { error } = await supabase.auth.updateUser({
        password,
    });
    if (error) throw error;
}

/**
 * Sign in with Google (OAuth)
 * 
 * CRITICAL: Uses /auth/callback for redirect URL
 * - Local: http://localhost:3000/auth/callback
 * - Production: https://stream.dominion-city.workers.dev/auth/callback
 * 
 * This URL MUST be in Supabase Dashboard → Authentication → URL Configuration → Redirect URLs
 * 
 * Required URLs in Supabase:
 * - http://localhost:3000/auth/callback
 * - https://stream.dominion-city.workers.dev/auth/callback
 */
export async function signInWithGoogle() {
    const redirectUrl = getOAuthRedirectUrl('/auth/callback');

    if (!redirectUrl) {
        throw new Error('Cannot determine redirect URL - window is not available');
    }

    // Log the redirect URL being used (for debugging)
    console.log('[OAuth] Using redirect URL:', redirectUrl);

    const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
            redirectTo: redirectUrl,
        },
    });

    if (error) throw error;
    return data;
}

/**
 * Send Magic Link
 */
export async function sendMagicLink(email: string) {
    // CRITICAL: Always use the active browser origin so the link returns to
    // the same domain that requested it, and always land on the auth callback
    // route that completes the session.
    const redirectUrl = getOAuthRedirectUrl('/auth/callback');

    if (!redirectUrl) {
        throw new Error('Cannot determine redirect URL - window is not available');
    }

    const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
            emailRedirectTo: redirectUrl,
        },
    });

    if (error) throw error;
}
